import { useState } from 'react';
import { Search, MapPin, Loader2 } from 'lucide-react';
import { Button } from '@/propertyai/components/ui/button';
import { Input } from '@/propertyai/components/ui/input';
import { Label } from '@/propertyai/components/ui/label';
import { Card } from '@/propertyai/components/ui/card';
import { Separator } from '@/propertyai/components/ui/separator';
import AVMWidget from '@/propertyai/components/AVMWidget';
import PropertyCard from '@/propertyai/components/PropertyCard';
import { getValuation, searchProperties } from '@/propertyai/lib/api';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/propertyai/components/ui/collapsible';
import { ChevronDown } from 'lucide-react';

export default function ZestimatePage() {
  const [city, setCity] = useState('');
  const [beds, setBeds] = useState('');
  const [baths, setBaths] = useState('');
  const [sqft, setSqft] = useState('');
  const [yearBuilt, setYearBuilt] = useState('');
  const [propertyType, setPropertyType] = useState('house');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [comps, setComps] = useState([]);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const handleEstimate = async (e) => {
    e.preventDefault();
    if (!city || !beds || !sqft) return;

    setLoading(true);
    try {
      const res = await getValuation({
        city: city,
        beds: parseInt(beds),
        baths: parseFloat(baths) || 2,
        sqft: parseInt(sqft),
        lot_size: parseInt(sqft) * 2,
        year_built: parseInt(yearBuilt) || 2000,
        property_type: propertyType,
      });
      setResult(res.data);

      // Also fetch comparable properties
      const compRes = await searchProperties({
        city: city,
        beds_min: Math.max(1, parseInt(beds) - 1),
        status: 'active',
        limit: 4,
      });
      setComps(compRes.data.properties || []);
    } catch (e) {
      console.error('Valuation error', e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-semibold font-['Space_Grotesk'] tracking-tight">PropertyAI Zestimate</h1>
          <p className="text-muted-foreground mt-2">Get an instant AI-powered property value estimate</p>
        </div>

        <Card className="rounded-2xl border border-border bg-background p-6 mb-8">
          <form onSubmit={handleEstimate}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div className="sm:col-span-3">
                <Label className="text-sm">City</Label>
                <div className="relative mt-1.5">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Enter city (e.g., Austin, Miami, Seattle)"
                    className="pl-10 rounded-xl"
                    data-testid="zestimate-city-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm">Bedrooms</Label>
                <Input type="number" value={beds} onChange={(e) => setBeds(e.target.value)} placeholder="3" className="mt-1.5 rounded-xl" data-testid="zestimate-beds-input" />
              </div>
              <div>
                <Label className="text-sm">Bathrooms</Label>
                <Input type="number" step="0.5" value={baths} onChange={(e) => setBaths(e.target.value)} placeholder="2" className="mt-1.5 rounded-xl" />
              </div>
              <div>
                <Label className="text-sm">Square Feet</Label>
                <Input type="number" value={sqft} onChange={(e) => setSqft(e.target.value)} placeholder="1,800" className="mt-1.5 rounded-xl" data-testid="zestimate-sqft-input" />
              </div>
            </div>

            <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen}>
              <CollapsibleTrigger className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3">
                <ChevronDown className={`w-4 h-4 transition-transform ${detailsOpen ? 'rotate-180' : ''}`} />
                More details (optional)
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label className="text-sm">Year Built</Label>
                    <Input type="number" value={yearBuilt} onChange={(e) => setYearBuilt(e.target.value)} placeholder="2000" className="mt-1.5 rounded-xl" />
                  </div>
                  <div>
                    <Label className="text-sm">Property Type</Label>
                    <select
                      value={propertyType}
                      onChange={(e) => setPropertyType(e.target.value)}
                      className="mt-1.5 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
                    >
                      <option value="house">House</option>
                      <option value="condo">Condo</option>
                      <option value="townhouse">Townhouse</option>
                    </select>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>

            <Button type="submit" className="w-full rounded-xl" disabled={loading || !city || !beds || !sqft} data-testid="zestimate-submit-button">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Estimating...</> : <><Search className="w-4 h-4 mr-2" /> Get Estimate</>}
            </Button>
          </form>
        </Card>

        {/* Results */}
        {result && (
          <div className="space-y-6">
            <AVMWidget valuation={result} priceHistory={result.historical_values} />

            {comps.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold mb-3">Similar Properties in {city}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {comps.map(prop => (
                    <PropertyCard key={prop.id} property={prop} compact />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

